<template>
  <div class="flex-grow-1 d-flex flex-column overflow-hidden">
    <!-- 搜索条件卡片 -->
    <v-card class="rounded-xl card-bg card-bd flex-shrink-0 px-6 py-5 mb-3" variant="elevated" elevation="0">
      <div class="d-flex align-center flex-wrap ga-6">
        <div class="search-item">
          <span class="search-label">组织名称</span>
          <v-text-field v-model="queryParams.deptName" placeholder="请输入组织名称" variant="outlined" density="compact"
            hide-details rounded="xl" class="search-input" @keyup.enter="handleQuery" @input="handleQuery" />
        </div>
        <div class="search-item">
          <span class="search-label">状态</span>
          <v-select v-model="queryParams.status" :items="statusOptions" item-title="label" item-value="value"
            placeholder="请选择" variant="outlined" density="compact" hide-details rounded="xl"
            class="search-input--select" clearable @update:model-value="handleQuery" />
        </div>

        <div class="search-actions d-flex ga-3 ml-auto">
          <v-btn color="#2655f1" rounded="xl" @click="handleQuery" class="action-btn">
            <v-icon start icon="mdi-magnify" />查询
          </v-btn>
          <v-btn variant="outlined" color="#2655f1" rounded="xl" @click="resetQuery" class="action-btn">
            <v-icon start icon="mdi-refresh" />重置
          </v-btn>
          <v-btn v-hasPermi="['system:dept:add']" variant="outlined" color="#2655f1" rounded="xl"
            @click="openDeptDialog(undefined, undefined)" class="action-btn">
            <v-icon start icon="mdi-plus" />新增组织
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- 表格卡片 -->
    <v-card class="rounded-xl card-bg card-bd card-table flex-grow-1 d-flex flex-column overflow-hidden"
      variant="elevated" elevation="0" :loading="loading">
      <div class="table-wrapper flex-grow-1 overflow-hidden d-flex flex-column">
        <v-table fixed-header height="100%" style="width: 100%; background: transparent" class="gm-table border-none">
          <thead>
            <tr>
              <th style="width: 500px">组织名称</th>
              <th style="width: 220px">显示顺序</th>
              <th style="width: 220px">状态</th>
              <th style="width: 320px">创建时间</th>
              <th style="width: 340px" class="text-center">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in visibleRows" :key="row.deptId">
              <td>
                <div class="org-name-cell">
                  <div class="org-name-inner" :style="{ paddingLeft: row.level * 24 + 'px' }">
                    <v-btn v-if="row.hasChildren" variant="text" density="compact" icon class="org-expand-btn mr-1"
                      :class="{ expanded: row.isExpanded }" @click="toggleExpand(row.deptId)">
                      <v-icon size="20" color="#2655f1">mdi-chevron-right</v-icon>
                    </v-btn>
                    <div v-else class="org-expand-placeholder" />
                    <span :style="{
                      fontWeight: row.level === 0 ? '500' : '500',
                      color: row.level === 0 ? 'inherit' : 'inherit',
                    }">
                      {{ row.deptName || "--" }}
                    </span>
                    <v-chip size="small" :color="deptIsOuter(row) ? '#FF6A46' : '#2655f1'" variant="tonal"
                      class="ml-2 org-type-chip">
                      {{ deptIsOuter(row) ? "外部" : "内部" }}
                    </v-chip>
                  </div>
                </div>
              </td>
              <td class="text-medium-emphasis">{{ row.orderNum ?? "--" }}</td>
              <td>
                <v-chip :color="String(row.status) === '1' ? '#FF6A46' : '#2DAE55'" variant="tonal" class="status-chip">
                  {{ String(row.status) === "1" ? "停用" : "正常" }}
                </v-chip>
              </td>
              <td class="text-medium-emphasis">
                {{ formatDateSafe(row.createTime) }}
              </td>
              <td>
                <div class="d-flex ga-1">
                  <v-btn v-hasPermi="['system:dept:add']" variant="text" color="#2655f1" rounded="xl"
                    @click="openDeptDialog(undefined, row)">新增下级</v-btn>
                  <v-btn v-hasPermi="['system:dept:edit']" variant="text" color="#2655f1" rounded="xl"
                    @click="openDeptDialog(row.deptId)">修改</v-btn>
                  <v-btn v-hasPermi="['system:dept:selectStation']" variant="text" color="#2655f1" rounded="xl"
                    @click="openStationView(row)">关联电站</v-btn>
                  <v-btn v-hasPermi="['system:dept:remove']" variant="text" color="#FF6A46" rounded="xl"
                    @click="onDelete(row)">删除</v-btn>
                </div>
              </td>
            </tr>
          </tbody>
        </v-table>
      </div>
      <div v-if="!loading && visibleRows.length === 0" class="text-center py-12 text-medium-emphasis">
        暂无组织数据
      </div>
      <div v-if="loading" class="text-center py-12 text-medium-emphasis">
        加载中…
      </div>
    </v-card>

    <!-- 删除确认弹窗 -->
    <v-dialog v-model="deleteDialogVisible" max-width="420px" persistent class="gm-dialog">
      <v-card class="rounded-30 overflow-hidden dialog-card">
        <div class="gm-dialog-header d-flex align-center px-8 py-6">
          <div class="text-h5">
            <span style="color: #3a68ff">删除</span><span>确认</span>
          </div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="rgba(46, 46, 46, 0.5)" @click="deleteDialogVisible = false" />
        </div>
        <v-card-text class="pa-8 pt-2 pb-4">
          <div class="text-body-1">确认删除「{{ deleteTargetName }}」？</div>
        </v-card-text>
        <v-card-actions class="px-8 pb-8 pt-0 justify-end">
          <v-btn variant="flat" color="#D9D9D9" class="gm-btn-secondary px-10 mr-4"
            @click="deleteDialogVisible = false">取消</v-btn>
          <v-btn color="#3a68ff" variant="flat" class="gm-btn-primary px-10 text-white" :loading="deleteLoading"
            @click="doDelete">确定</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 新增/编辑弹窗 -->
    <v-dialog v-model="dialogVisible" max-width="840px" persistent class="gm-dialog">
      <v-card class="rounded-30 overflow-hidden dialog-card">
        <div class="gm-dialog-header d-flex align-center px-8 py-6">
          <div class="text-h5">
            <span style="color: #2655f1">{{
              dialogMode === "add" ? "新增" : "修改"
            }}</span>
            <span>组织</span>
          </div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="rgba(46, 46, 46, 0.5)" @click="dialogVisible = false" />
        </div>

        <v-card-text class="pa-8 pt-0 pb-4 scrollbar-hide" style="max-height: 88vh; overflow-y: auto">
          <v-form>
            <div class="form-section-title">组织信息</div>
            <div class="form-grid-2">
              <div class="form-group grid-full">
                <label>上级组织</label>
                <div class="dialog-form__control">
                  <el-tree-select v-model="form.parentId" :data="parentDeptOptions" :teleported="false" :props="{
                    label: 'label',
                    children: 'children',
                    value: 'id',
                  }" placeholder="请选择上级组织" check-strictly class="gm-tree-select" filterable clearable
                    default-expand-all :loading="parentDeptLoading" />
                </div>
              </div>

              <div class="form-group">
                <label>组织名称 <span class="required-star">*</span></label>
                <v-text-field v-model="form.deptName" placeholder="请输入组织名称" variant="outlined" density="compact"
                  class="gm-input" hide-details />
              </div>

              <div class="form-group">
                <label>显示顺序</label>
                <v-text-field v-model.number="form.orderNum" type="number" placeholder="请输入排序" variant="outlined"
                  density="compact" class="gm-input" hide-details />
              </div>

              <div class="form-group">
                <label>组织类型</label>
                <v-radio-group v-model="form.type" inline hide-details class="gm-radio-group">
                  <div class="d-flex ga-6 w-100">
                    <v-radio label="内部" value="inner" class="gm-radio-card flex-1" color="#2655F1" />
                    <v-radio label="外部" value="outer" class="gm-radio-card flex-1" color="#2655F1" />
                  </div>
                </v-radio-group>
              </div>

              <div class="form-group">
                <label>组织状态 <span class="required-star">*</span></label>
                <v-radio-group v-model="form.status" inline hide-details class="gm-radio-group">
                  <div class="d-flex ga-6 w-100">
                    <v-radio label="正常" value="0" class="gm-radio-card flex-1" color="#2655F1" />
                    <v-radio label="停用" value="1" class="gm-radio-card flex-1" color="#2655F1" />
                  </div>
                </v-radio-group>
              </div>

              <div class="form-group grid-full">
                <div class="form-section-title mb-2">关联电站</div>
                <div class="d-flex flex-wrap align-center ga-4">
                  <v-text-field v-model="stationKeyword" placeholder="搜索电站名称" variant="outlined" density="compact"
                    class="gm-input" hide-details style="max-width: 260px" @keyup.enter="applyStationFilter" />
                  <v-select v-model="stationProvince" :items="stationProvinceOptions" item-title="label"
                    item-value="value" placeholder="按省份筛选" variant="outlined" density="compact"
                    class="gm-input station-province-select" hide-details clearable style="max-width: 180px"
                    @update:model-value="applyStationFilter" />
                  <v-checkbox :model-value="isAllStationChecked" :indeterminate="isStationIndeterminate" label="全选"
                    density="compact" color="#2655f1" hide-details @update:model-value="toggleAllStations" />
                </div>
                <div class="station-list-box rounded-xl border-none">
                  <div v-if="stationLoading" class="station-loading-state text-medium-emphasis">
                    <v-progress-circular indeterminate color="#2655f1" size="24" class="mr-2" />
                    电站加载中…
                  </div>
                  <div v-else class="station-select-list">
                    <label v-for="station in filteredStations" :key="station._sid" class="station-select-item">
                      <v-checkbox :model-value="selectedStationIds.has(stationIdOf(station) as any)" density="compact"
                        color="#2655f1" hide-details
                        @update:model-value="toggleStation(stationIdOf(station) as any, $event)" />
                      <span class="station-select-name">{{ station._name }}</span>
                    </label>
                  </div>
                  <div v-if="!stationLoading && filteredStations.length === 0"
                    class="text-center text-medium-emphasis py-6">
                    无匹配电站
                  </div>
                </div>
              </div>

              <template v-if="form.type === 'outer'">
                <div class="form-group grid-full">
                  <div class="form-section-title mb-0">企业信息</div>
                </div>
                <div class="form-group grid-full">
                  <div class="d-flex align-center ga-3">
                    <label class="mb-0">虚拟电厂服务</label>
                    <v-switch v-model="form.isJoinVpp" color="#13ce66" hide-details inset />
                  </div>
                </div>
                <div class="form-group">
                  <label>省 <span class="required-star">*</span></label>
                  <v-select v-model="form.province" :items="provinceOptions" item-title="label" item-value="value"
                    placeholder="请选择省份" variant="outlined" density="compact" class="gm-input" hide-details
                    @update:model-value="onProvinceChange" />
                </div>
                <div class="form-group">
                  <label>市 <span class="required-star">*</span></label>
                  <v-select v-model="form.city" :items="cityOptions" item-title="label" item-value="value"
                    placeholder="请选择市" variant="outlined" density="compact" class="gm-input" hide-details />
                </div>
                <div class="form-group">
                  <label>邮箱</label><v-text-field v-model="form.email" variant="outlined" density="compact"
                    class="gm-input" hide-details placeholder="请输入邮箱" />
                </div>
                <div class="form-group">
                  <label>地址</label><v-text-field v-model="form.address" variant="outlined" density="compact"
                    class="gm-input" hide-details placeholder="请输入地址信息" />
                </div>
                <div class="form-group">
                  <label>企业名称</label>
                  <v-text-field v-model="form.deptName" variant="outlined" density="compact" class="gm-input"
                    hide-details readonly />
                </div>
                <div class="form-group">
                  <label>企业简称 <span v-if="needOuterServiceRequired" class="required-star">*</span></label>
                  <v-text-field v-model="form.deptShortName" variant="outlined" density="compact" class="gm-input"
                    hide-details placeholder="请输入企业简称" />
                </div>
                <div class="form-group">
                  <label>组织机构代码 <span v-if="needOuterServiceRequired" class="required-star">*</span></label>
                  <v-text-field v-model="form.businessLicenseCode" variant="outlined" density="compact" class="gm-input"
                    hide-details placeholder="请输入组织机构代码" />
                </div>
                <div class="form-group">
                  <label>联系人 <span v-if="needOuterServiceRequired" class="required-star">*</span></label><v-text-field
                    v-model="form.leader" variant="outlined" density="compact" class="gm-input" hide-details
                    placeholder="请输入联系人" />
                </div>
                <div class="form-group">
                  <label>联系电话 <span v-if="needOuterServiceRequired" class="required-star">*</span></label><v-text-field
                    v-model="form.phone" variant="outlined" density="compact" class="gm-input" hide-details
                    placeholder="请输入联系电话" />
                </div>
                <div class="form-group">
                  <label>联系人职务 <span v-if="needOuterServiceRequired" class="required-star">*</span></label><v-text-field
                    v-model="form.duties" variant="outlined" density="compact" class="gm-input" hide-details
                    placeholder="请输入联系人职务" />
                </div>
                <div class="form-group">
                  <label>合同有效期 <span v-if="needOuterServiceRequired" class="required-star">*</span></label>
                  <div class="gm-el-control">
                    <el-date-picker v-model="form.contractExpireTimeStr" value-format="YYYY-MM-DD HH:mm:ss"
                      type="datetime" placeholder="请选择合同有效期" format="YYYY-MM-DD HH:mm:ss" style="width: 100%"
                      :teleported="false" placement="top-start" popper-class="gm-date-popper" />
                  </div>
                </div>
                <div class="form-group">
                  <label>分成比例 <span v-if="needOuterServiceRequired" class="required-star">*</span></label>
                  <div class="d-flex align-center ga-2 gm-el-control">
                    <el-input-number v-model="form.proportion" :controls="false" placeholder="请输入0~100%" :precision="1"
                      :step="0.1" :max="100" :min="0" style="width: 100%" />
                    <span>%</span>
                  </div>
                </div>
                <div class="form-group grid-full">
                  <div class="upload-row">
                    <div class="upload-item">
                      <label>首页 <span v-if="needOuterServiceRequired" class="required-star">*</span></label>
                      <el-upload :http-request="(o: any) => requestContractUpload(o, 'first')" :show-file-list="false"
                        :before-upload="beforeContractUpload">
                        <v-btn color="#22b322" variant="flat" class="text-white">上传首页</v-btn>
                      </el-upload>
                      <div v-if="form.contractFirstpageImgUrl" class="upload-preview">
                        <img :src="form.contractFirstpageImgUrl" alt="首页预览" />
                      </div>
                      <div class="text-caption text-medium-emphasis mt-2">图片文件大小不要超过10MB.</div>
                    </div>
                    <div class="upload-item">
                      <label>印章页 <span v-if="needOuterServiceRequired" class="required-star">*</span></label>
                      <el-upload :http-request="(o: any) => requestContractUpload(o, 'stamp')" :show-file-list="false"
                        :before-upload="beforeContractUpload">
                        <v-btn color="#22b322" variant="flat" class="text-white">上传印章页</v-btn>
                      </el-upload>
                      <div v-if="form.contractStamppageImgUrl" class="upload-preview">
                        <img :src="form.contractStamppageImgUrl" alt="印章页预览" />
                      </div>
                      <div class="text-caption text-medium-emphasis mt-2">图片文件大小不要超过10MB.</div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-8 pb-8 pt-0 justify-end">
          <v-btn variant="flat" color="#D9D9D9" class="gm-btn-secondary px-10 mr-4"
            @click="dialogVisible = false">取消</v-btn>
          <v-btn color="#2655f1" variant="flat" class="gm-btn-primary px-10 text-white" :loading="saving"
            @click="submitForm">确定</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 查看关联电站弹窗 -->
    <v-dialog v-model="stationViewVisible" max-width="740px" scrollable class="gm-dialog">
      <v-card class="rounded-30 overflow-hidden dialog-card">
        <div class="gm-dialog-header d-flex align-center px-8 py-6">
          <div class="text-h5">
            <span style="color: #2655f1">关联</span><span>电站</span>
          </div>
          <v-spacer /><v-btn icon="mdi-close" variant="text" color="rgba(46, 46, 46, 0.5)"
            @click="stationViewVisible = false" />
        </div>
        <v-card-text class="pa-8 pt-0">
          <div class="d-flex align-center justify-space-between flex-wrap ga-2 text-subtitle-1 mb-4">
            <div>
              所在组织:
              <span class="font-weight-bold" style="color: #2655f1">{{
                stationViewTitle
              }}</span>
            </div>
            <div class="text-body-2 text-medium-emphasis">
              电站总数:
              <span class="font-weight-bold" style="color: #2655f1">
                {{ stationViewTotal }}
              </span>
              座
            </div>
          </div>
          <v-table class="gm-table-sub" style="background: transparent; width: 100%">
            <thead>
              <tr>
                <th>电站名称</th>
                <th style="width: 180px">省市</th>
                <!-- <th style="width: 150px">状态</th> -->
              </tr>
            </thead>
            <tbody>
              <tr v-for="station in stationViewRows" :key="station._sid">
                <td>{{ station._name }}</td>
                <td>
                  {{
                    [station.province, station.city]
                      .filter(Boolean)
                      .join("/") || "--"
                  }}
                </td>
                <!-- <td>
                    <v-chip size="x-small" density="compact" variant="tonal" color="primary">{{ station.stationStateName
                      ||
                      "--" }}</v-chip>
                  </td> -->
              </tr>
            </tbody>
          </v-table>
          <div v-if="stationViewRows.length === 0" class="text-center text-medium-emphasis py-12">
            该组织暂无关联电站
          </div>
        </v-card-text>
        <v-card-actions class="px-8 pb-8 pt-0 justify-end">
          <v-btn variant="flat" color="#D9D9D9" class="gm-btn-secondary px-10"
            @click="stationViewVisible = false">关闭</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, reactive, ref } from "vue";
import { eui } from "ems-ui";
import busRequest from "@/utils/request/bus";
import { formatDate } from "@/utils";
import { handleTree } from "@/utils/ruoyi";
import {
  addDeptEcenter,
  delDeptEcenter,
  deptStationEcenter,
  getDeptEcenter,
  listDeptEcenter,
  listStationEcenter,
  relationStationEcenter,
  treeselectDeptEcenter,
  updateDeptEcenter,
} from "@/api/ecenter";

const loading = ref(false);
const deptRoots = ref<any[]>([]);
const expandedIds = ref<Array<number | string>>([]);

const queryParams = reactive({
  deptName: undefined,
  status: undefined,
});

const statusOptions = [
  { label: "正常", value: "0" },
  { label: "停用", value: "1" },
];

function formatDateSafe(v: any) {
  return v ? formatDate(v) : "--";
}

/** 与 ecenter-ui 一致：接口字段为 type，取值 inner | outer */
function normalizeDeptType(d: any): "inner" | "outer" {
  const t = d?.type;
  if (t === "outer" || String(t) === "outer") return "outer";
  if (t === "inner" || String(t) === "inner") return "inner";
  if (d?.orgType === "external") return "outer";
  if (d?.orgType === "internal") return "inner";
  return "inner";
}

function deptIsOuter(row: any) {
  return normalizeDeptType(row) === "outer";
}

function normalizeTreeRows(nodes: any[], level = 0, out: any[] = []) {
  nodes.forEach((node) => {
    const hasChildren =
      Array.isArray(node.children) && node.children.length > 0;
    const isExpanded = expandedIds.value.some(
      (id) => String(id) === String(node.deptId),
    );
    out.push({ ...node, level, hasChildren, isExpanded });
    if (hasChildren && isExpanded)
      normalizeTreeRows(node.children, level + 1, out);
  });
  return out;
}

const visibleRows = computed(() => normalizeTreeRows(deptRoots.value));

function toggleExpand(deptId: number | string) {
  const idx = expandedIds.value.findIndex(
    (id) => String(id) === String(deptId),
  );
  if (idx >= 0) expandedIds.value.splice(idx, 1);
  else expandedIds.value.push(deptId);
}

async function fetchDeptTree() {
  loading.value = true;
  const params: any = {};
  if (queryParams.deptName) params.deptName = queryParams.deptName;
  if (queryParams.status) params.status = queryParams.status;

  try {
    const res = await listDeptEcenter(params);
    const rows = Array.isArray(res?.data)
      ? res.data
      : Array.isArray(res)
        ? res
        : [];
    deptRoots.value = handleTree(
      rows,
      "deptId",
      "parentId",
      "children",
    ) as any[];
    if (expandedIds.value.length === 0)
      expandedIds.value = deptRoots.value.map((x) => x.deptId);
  } finally {
    loading.value = false;
  }
}

function handleQuery() {
  fetchDeptTree();
}
function resetQuery() {
  queryParams.deptName = undefined;
  queryParams.status = undefined;
  handleQuery();
}

// ---------- 弹窗逻辑 ----------
const dialogVisible = ref(false);
const dialogMode = ref<"add" | "edit">("add");
const saving = ref(false);
const editingDeptId = ref<any>(undefined);
const form = reactive<any>({
  deptId: undefined,
  parentId: 0,
  deptName: "",
  orderNum: 0,
  status: "0",
  leader: "",
  phone: "",
  email: "",
  province: "",
  city: "",
  /** 与 ecenter 一致：提交时由 city 字段 `regionCode|城市名` 拆出；详情无复合值时兜底 */
  regionCode: "",
  address: "",
  type: "inner",
  deptShortName: "",
  businessLicenseCode: "",
  duties: "",
  proportion: 0,
  contractExpireTimeStr: null,
  contractFirstpageImgUrl: "",
  contractStamppageImgUrl: "",
  isJoinVpp: false,
});
const parentDeptOptions = ref<any[]>([]);
const parentDeptLoading = ref(false);
async function fetchParentOptions() {
  const res = await treeselectDeptEcenter();
  const trees = Array.isArray(res?.data)
    ? res.data
    : Array.isArray(res)
      ? res
      : [];
  parentDeptOptions.value = trees;
}

const allStations = ref<any[]>([]);
const filteredStations = ref<any[]>([]);
const stationLoading = ref(false);
const stationKeyword = ref("");
const stationProvince = ref("");
const selectedStationIds = ref<Set<any>>(new Set());
const provinceOptions = ref<Array<{ label: string; value: string }>>([]);
const cityOptions = ref<Array<{ label: string; value: string }>>([]);
const needOuterServiceRequired = computed(
  () => form.type === "outer" && !!form.isJoinVpp,
);
const stationProvinceOptions = computed(() => {
  if (provinceOptions.value.length > 0) {
    return [{ label: "全部省份", value: "" }, ...provinceOptions.value];
  }
  const set = new Set<string>();
  allStations.value.forEach((s: any) => {
    if (s.province) set.add(String(s.province));
  });
  return [{ label: "全部省份", value: "" }, ...[...set].map((p) => ({ label: p, value: p }))];
});
const cityTreeRaw = ref<any[]>([]);
async function fetchProvinceCityOptions() {
  try {
    const r = await fetch("/data/city.json");
    const list = await r.json();
    cityTreeRaw.value = Array.isArray(list) ? list : [];
    provinceOptions.value = cityTreeRaw.value
      .map((p: any) => ({ label: String(p?.value || ""), value: String(p?.value || "") }))
      .filter((x: any) => x.value);
  } catch {
    cityTreeRaw.value = [];
    provinceOptions.value = [];
  }
}
function stationIdOf(s: any) {
  return s?.stationId ?? s?.id ?? s?.stationid;
}

async function fetchAllStations() {
  const res: any = await listStationEcenter({ pageNum: 1, pageSize: 9999 });
  const rows = res?.rows || res?.data?.rows || [];
  allStations.value = rows.map((s: any) => ({
    ...s,
    _sid: stationIdOf(s),
    _name: s?.name ?? s?.stationName ?? "--",
  }));
  applyStationFilter();
}

function applyStationFilter() {
  const kw = stationKeyword.value.trim().toLowerCase();
  filteredStations.value = allStations.value.filter((s) => {
    const byName = !kw || String(s._name).toLowerCase().includes(kw);
    const byProvince = !stationProvince.value || String(s.province || "") === stationProvince.value;
    return byName && byProvince;
  });
}

const isAllStationChecked = computed(
  () =>
    filteredStations.value.length > 0 &&
    filteredStations.value.every((s) =>
      selectedStationIds.value.has(stationIdOf(s)),
    ),
);
const isStationIndeterminate = computed(() => {
  const checked = filteredStations.value.filter((s) =>
    selectedStationIds.value.has(stationIdOf(s)),
  ).length;
  return checked > 0 && checked < filteredStations.value.length;
});

function toggleAllStations(v: any) {
  filteredStations.value.forEach((s) => {
    v
      ? selectedStationIds.value.add(stationIdOf(s))
      : selectedStationIds.value.delete(stationIdOf(s));
  });
}
function toggleStation(sid: any, v: any) {
  v ? selectedStationIds.value.add(sid) : selectedStationIds.value.delete(sid);
}

/** ecenter-ui：市选项 value 为 regionCode|城市名，提交时再拆成 regionCode + city */
function cityOptionValue(node: any) {
  const code = String(node?.code || "");
  const name = String(node?.value || "");
  return `${code.slice(0, 4)}|${name}`;
}

function onProvinceChange(v: string) {
  if (cityTreeRaw.value.length > 0) {
    const item = cityTreeRaw.value.find(
      (p: any) => String(p?.value || "") === String(v || ""),
    );
    const children = item?.children || [];
    cityOptions.value = children.map((c: any) => ({
      label: String(c?.value || ""),
      value: cityOptionValue(c),
    }));
    if (cityOptions.value.length) {
      if (!cityOptions.value.some((c) => c.value === form.city))
        form.city = cityOptions.value[0].value;
    } else form.city = "";
    return;
  }
  const cities = allStations.value
    .filter((s: any) => String(s.province || "") === String(v || ""))
    .map((s: any) => String(s.city || ""))
    .filter(Boolean);
  cityOptions.value = [...new Set(cities)].map((c) => ({ label: String(c), value: String(c) }));
  if (!cityOptions.value.some((c) => c.value === form.city)) form.city = "";
}

function splitCityForSubmit(raw: string) {
  const s = String(raw || "");
  const i = s.indexOf("|");
  if (i < 0)
    return { regionCode: String(form.regionCode || "").trim(), city: s.trim() };
  return { regionCode: s.slice(0, i).trim(), city: s.slice(i + 1).trim() };
}

function beforeContractUpload(file: File) {
  const isImg = file.type.startsWith("image/");
  const isLt10M = file.size / 1024 / 1024 < 10;
  if (!isImg) eui.showToast("请上传图片文件", "error");
  if (!isLt10M) eui.showToast("图片文件大小不要超过10MB", "error");
  return isImg && isLt10M;
}

function extractContractImgUrl(payload: any) {
  if (!payload) return "";
  let p = payload;
  if (typeof payload === "string") {
    try {
      p = JSON.parse(payload);
    } catch {
      return "";
    }
  }
  return (
    p?.contractImgUrl ||
    p?.data?.contractImgUrl ||
    p?.response?.contractImgUrl ||
    ""
  );
}

function onContractUploadSuccess(res: any, type: "first" | "stamp") {
  const url = extractContractImgUrl(res);
  if (type === "first") form.contractFirstpageImgUrl = url;
  else form.contractStamppageImgUrl = url;
}

async function requestContractUpload(options: any, type: "first" | "stamp") {
  const formData = new FormData();
  formData.append("contractFile", options.file);
  try {
    const res: any = await busRequest({
      url: "ecenter/system/dept/contractFileUpload",
      method: "post",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    });
    const url = extractContractImgUrl(res);
    if (!url) {
      eui.showToast("上传成功但未获取到图片地址", "error");
      options.onError?.(new Error("missing contractImgUrl"));
      return;
    }
    if (type === "first") form.contractFirstpageImgUrl = url;
    else form.contractStamppageImgUrl = url;
    options.onSuccess?.(res, options.file);
  } catch (err) {
    eui.showToast("上传失败，请重试", "error");
    options.onError?.(err);
  }
}

async function loadParentOptions(parentRow?: any) {
  parentDeptLoading.value = true;
  try {
    await fetchParentOptions();
    if (!editingDeptId.value && !parentRow?.deptId && parentDeptOptions.value.length > 0) {
      form.parentId = parentDeptOptions.value[0]?.id ?? parentDeptOptions.value[0]?.deptId ?? 0;
    }
    if (parentRow?.deptId) form.parentId = parentRow.deptId;
  } finally {
    parentDeptLoading.value = false;
  }
}

async function loadDialogStations(deptId?: any) {
  stationLoading.value = true;
  try {
    await fetchAllStations();
    if (deptId) {
      const stationRes: any = await deptStationEcenter(deptId);
      const ids =
        stationRes?.data?.checkedStationKeys ||
        stationRes?.checkedStationKeys ||
        (Array.isArray(stationRes?.data)
          ? stationRes.data.map((x: any) => x.stationId)
          : []);
      selectedStationIds.value = new Set(ids);
    }
  } finally {
    stationLoading.value = false;
  }
}

async function openDeptDialog(deptId?: any, parentRow?: any) {
  dialogMode.value = deptId ? "edit" : "add";
  editingDeptId.value = deptId;
  Object.assign(form, {
    deptId: undefined,
    parentId: 0,
    deptName: "",
    orderNum: 1,
    status: "0",
    leader: "",
    phone: "",
    email: "",
    province: "",
    city: "",
    regionCode: "",
    address: "",
    type: "inner",
    deptShortName: "",
    businessLicenseCode: "",
    duties: "",
    proportion: 0,
    contractExpireTimeStr: null,
    contractFirstpageImgUrl: "",
    contractStamppageImgUrl: "",
    isJoinVpp: false,
  });
  delete form.orgType;
  selectedStationIds.value.clear();
  parentDeptOptions.value = [];
  allStations.value = [];
  filteredStations.value = [];
  stationKeyword.value = "";
  stationProvince.value = "";
  if (parentRow?.deptId) form.parentId = parentRow.deptId;
  if (deptId) {
    const res = await getDeptEcenter(deptId);
    const d = res?.data || {};
    Object.assign(form, {
      ...d,
      status: String(d.status ?? "0"),
      type: normalizeDeptType(d),
      isJoinVpp: Boolean(d?.isJoinVpp),
      proportion: Number((d?.proportion ?? 0) * 100),
      regionCode: d.regionCode ?? "",
    });
    delete form.orgType;
    onProvinceChange(form.province);
    if (d.regionCode != null && d.city != null && String(d.city) !== "")
      form.city = `${d.regionCode}|${d.city}`;
  }
  dialogVisible.value = true;
  nextTick(() => {
    loadParentOptions(parentRow);
    loadDialogStations(deptId);
  });
}

async function submitForm() {
  const deptName = String(form.deptName || "").trim();
  if (!deptName) {
    eui.showToast("请输入组织名称", "error");
    return;
  }
  if (!String(form.status || "").trim()) {
    eui.showToast("请选择组织状态", "error");
    return;
  }
  if (form.type === "outer") {
    if (!String(form.province || "").trim()) {
      eui.showToast("请选择省", "error");
      return;
    }
    if (!String(form.city || "").trim()) {
      eui.showToast("请选择市", "error");
      return;
    }
    if (needOuterServiceRequired.value) {
      if (!String(form.deptShortName || "").trim()) {
        eui.showToast("请输入企业简称", "error");
        return;
      }
      if (!String(form.businessLicenseCode || "").trim()) {
        eui.showToast("请输入组织机构代码", "error");
        return;
      }
      if (!String(form.leader || "").trim()) {
        eui.showToast("请输入联系人", "error");
        return;
      }
      if (!String(form.phone || "").trim()) {
        eui.showToast("请输入联系电话", "error");
        return;
      }
      if (!String(form.duties || "").trim()) {
        eui.showToast("请输入联系人职务", "error");
        return;
      }
      if (!String(form.contractExpireTimeStr || "").trim()) {
        eui.showToast("请选择合同有效期", "error");
        return;
      }
      if (form.proportion === undefined || form.proportion === null || form.proportion === "") {
        eui.showToast("请输入分成比例", "error");
        return;
      }
      if (!String(form.contractFirstpageImgUrl || "").trim() || !String(form.contractStamppageImgUrl || "").trim()) {
        eui.showToast("请上传首页和印章页图片", "error");
        return;
      }
    }
  }
  saving.value = true;
  try {
    const payload: any = {
      ...form,
      orderNum: Number(form.orderNum),
      proportion: form.type === "outer" ? Number(form.proportion || 0) / 100 : undefined,
    };
    payload.contractExpireTimeStr = String(form.contractExpireTimeStr || "").trim() || null;
    delete payload.orgType;
    if (form.type === "outer") {
      const { regionCode, city: cityName } = splitCityForSubmit(String(form.city || ""));
      payload.regionCode = regionCode;
      payload.city = cityName.toLowerCase().includes("null") ? "null" : cityName;
    }
    if (dialogMode.value === "edit") {
      await updateDeptEcenter(payload);
      await relationStationEcenter(editingDeptId.value, [
        ...selectedStationIds.value,
      ]);
    } else {
      const res = await addDeptEcenter(payload);
      const nid = res?.data?.deptId || res?.data;
      if (nid) await relationStationEcenter(nid, [...selectedStationIds.value]);
    }
    eui.showToast("保存成功", "success");
    dialogVisible.value = false;
    fetchDeptTree();
  } finally {
    saving.value = false;
  }
}

const deleteDialogVisible = ref(false);
const deleteLoading = ref(false);
const deleteTargetRow = ref<any>(null);
const deleteTargetName = computed(() => deleteTargetRow.value?.deptName || "");

function onDelete(row: any) {
  deleteTargetRow.value = row;
  deleteDialogVisible.value = true;
}

async function doDelete() {
  if (!deleteTargetRow.value) return;
  deleteLoading.value = true;
  try {
    await delDeptEcenter(deleteTargetRow.value.deptId);
    eui.showToast("删除成功", "success");
    deleteDialogVisible.value = false;
    fetchDeptTree();
  } finally {
    deleteLoading.value = false;
  }
}

const stationViewVisible = ref(false);
const stationViewRows = ref<any[]>([]);
const stationViewTitle = ref("");
const stationViewTotal = ref(0);
async function openStationView(row: any) {
  stationViewTitle.value = row.deptName;
  const res: any = await deptStationEcenter(row.deptId);
  const ids = (
    res?.data?.checkedStationKeys ||
    res?.checkedStationKeys ||
    (Array.isArray(res?.data) ? res.data.map((x: any) => x.stationId) : [])
  ).map((x: any) => String(x));
  const uniqueIds = [...new Set(ids)];
  stationViewTotal.value = uniqueIds.length;
  if (allStations.value.length === 0) await fetchAllStations();
  stationViewRows.value = allStations.value.filter((s) =>
    uniqueIds.includes(String(s._sid)),
  );
  stationViewVisible.value = true;
}

fetchDeptTree();
fetchProvinceCityOptions();
</script>

<style scoped lang="scss">
.card-bg {
  background: #fff;
  backdrop-filter: blur(8px);
}

.card-bd {
  border-radius: 0 0 30px 30px !important;
}

.card-table {
  padding: 24px 16px 0;
  border-radius: 30px !important;
}

.search-item {
  display: flex;
  align-items: center;
  gap: 12px;

  .search-label {
    white-space: nowrap;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.7);
  }

  .search-input {
    width: 220px;
  }

  .search-input--select {
    width: 220px;
  }
}

.gm-table {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;

  :deep(.v-table__wrapper) {
    overflow-x: auto !important;
  }

  :deep(thead tr) {
    background: #f8fafc !important;
  }

  :deep(th) {
    font-weight: 600 !important;
    color: rgba(0, 0, 0, 0.8) !important;
    border: none !important;
    background: #f8fafc !important;
    height: 56px !important;
    padding: 0 24px !important;
    white-space: nowrap;
  }

  :deep(td) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.04) !important;
    height: 56px !important;
    padding: 0 24px !important;
    white-space: nowrap;
  }
}

.org-name-cell {
  display: flex;
  align-items: center;
  min-height: 48px;
}

.org-name-inner {
  display: flex;
  align-items: center;
  min-height: 48px;
}

.org-type-chip {
  height: 20px !important;
  border: 1px solid currentColor !important;
  font-weight: 500 !important;
  padding: 0 8px !important;
  opacity: 0.9;
}

.org-expand-btn {
  transition: transform 0.2s;

  &.expanded {
    transform: rotate(90deg);
  }
}

.org-expand-placeholder {
  width: 30px;
  flex-shrink: 0;
}

.status-chip {
  // font-weight: 600;
  min-width: 60px;
  height: 28px !important;
  line-height: 28px !important;
  justify-content: center;
  border: 1px solid currentColor !important;
}

.gm-dialog {
  :deep(.v-overlay__content) {
    border-radius: 30px !important;
  }
}

.dialog-card {
  border-radius: 30px !important;
  background: #fff;
}

.gm-dialog-header {
  background: linear-gradient(185deg, #2655f160 0%, #fff0 60%);
  position: relative;

  .text-h5 {
    font-size: 24px !important;
    font-weight: 500;
  }
}

.gm-input {
  :deep(.v-field) {
    background-color: rgba(239, 244, 251, 0.7) !important;
    border-radius: 15px !important;
    box-shadow: none !important;
    padding-inline: 4px !important;

    .v-field__outline {
      display: none !important;
    }
  }

  &.v-input--focused :deep(.v-field) {
    border-color: #2655f1 !important;
  }
}

.gm-radio-group {
  :deep(.v-selection-control-group) {
    width: 100%;
  }
}

.gm-radio-card {
  flex: 1;
  height: 42px;
  background-color: rgba(239, 244, 251, 0.7) !important;
  border-radius: 15px !important;
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex !important;
  align-items: center;
  justify-content: center;

  :deep(.v-selection-control) {
    width: 100%;
    height: 100%;
    margin: 0 !important;

    .v-selection-control__wrapper {
      display: none;
    }
  }

  &.v-selection-control--active {
    background-color: #2655f1 !important;
    box-shadow: 0 4px 12px rgba(38, 85, 241, 0.2) !important;

    :deep(.v-label) {
      color: #fff !important;
      font-weight: 600 !important;
    }
  }
}

:deep(.v-label) {
  font-size: 14px !important;
  color: #2e2e2e;
  margin: 0 !important;
}

.station-list-box {
  height: 280px;
  overflow-y: auto;
  background: rgba(239, 244, 251, 0.4);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.station-loading-state {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.station-select-list {
  padding: 8px 12px;
}

.station-select-item {
  display: flex;
  align-items: center;
  min-height: 36px;
}

.station-select-name {
  font-size: 14px;
  color: rgba(46, 46, 46, 0.9);
}

.gm-table-sub {
  :deep(th) {
    background: #f1f4f9 !important;
    font-size: 13px !important;
    height: 40px !important;
    padding: 0 16px !important;
    color: rgba(0, 0, 0, 0.6) !important;
  }

  :deep(td) {
    font-size: 13px !important;
    height: 40px !important;
    padding: 0 16px !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.02) !important;
  }
}

.form-grid-2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.grid-full {
  grid-column: span 2;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 10px;

  label {
    font-size: 14px;
    font-weight: 500;
    color: #2e2e2e;
  }
}

.form-section-title {
  grid-column: span 2;
  margin: 2px 0 2px;
  padding-left: 10px;
  border-left: 4px solid #2655f1;
  font-size: 16px;
  font-weight: 600;
  color: #1f2d3d;
}

.station-province-select {
  :deep(.v-field) {
    min-height: 40px !important;
    background-color: rgba(239, 244, 251, 0.7) !important;
    border-radius: 15px !important;
  }
}

.gm-el-control {
  :deep(.el-input__wrapper) {
    background-color: rgba(239, 244, 251, 0.7) !important;
    border-radius: 15px !important;
    box-shadow: none !important;
    border: 1px solid rgba(0, 0, 0, 0.05) !important;
    min-height: 40px;
  }
}

.gm-btn-primary,
.gm-btn-secondary {
  height: 42px !important;
  border-radius: 12px !important;
  font-size: 16px !important;
}

.gm-tree-select {
  width: 100%;

  :deep(.el-select__wrapper) {
    background-color: rgba(239, 244, 251, 0.7) !important;
    border-radius: 15px !important;
    box-shadow: none !important;
    border: 1px solid rgba(0, 0, 0, 0.05) !important;
    height: 40px !important;
    padding: 0 12px !important;

    &.is-focused {
      border-color: #2655f1 !important;
      background-color: #fff !important;
    }
  }

  :deep(.el-select__placeholder) {
    color: rgba(46, 46, 46, 0.5);
    font-size: 14px;
  }

  :deep(.el-select__selection) {
    font-size: 14px;
    color: rgba(46, 46, 46, 0.8);
  }
}

.required-star {
  color: #ff4d4f;
}

.upload-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}

.upload-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.upload-preview {
  width: 100%;
  max-width: 220px;
  height: 96px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  overflow: hidden;
  background: #f7f8fa;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

:deep(.gm-date-popper) {
  z-index: 4000 !important;
}
</style>
